import { Plus, Minus, Bell, Tag, Leaf, Package, ShieldAlert, X, CalendarDays, AlertTriangle } from 'lucide-react';
import { useState } from 'react';
import { useCart } from '../lib/cart';
import { useAuth } from '../lib/auth';
import { notifyStock, api } from '../lib/api';
import NearbyPharmacyAlert from './NearbyPharmacyAlert';
import { toast } from 'sonner';

const PACKAGING_LABELS = {
  original_pack: 'Original pack',
  paper_wrap: 'Paper wrap',
  polybag: 'Polybag',
  newspaper: 'Newspaper roll',
  cloth_bag: 'Cloth bag',
  byo: 'Bring your own',
  tiffin: 'Tiffin carrier',
};
function packagingLabel(p) { return PACKAGING_LABELS[p] || p; }

function _expiryStatus(useByIso) {
  if (!useByIso) return null;
  const d = new Date(useByIso);
  if (Number.isNaN(d.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const days = Math.round((d - today) / 86400000);
  const human = d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  if (days < 0) return { tone: 'expired', label: `Expired ${human}`, days };
  if (days <= 7) return { tone: 'soon', label: `Use by ${human} · ${days}d left`, days };
  if (days <= 30) return { tone: 'warn', label: `Use by ${human}`, days };
  return { tone: 'ok', label: `Use by ${human}`, days };
}

function fmtDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
}

export default function ProductCard({ product, store }) {
  const cart = useCart();
  const { user } = useAuth();
  const inCart = cart.items.find(i => i.product.id === product.id);
  // Treat product as in-stock unless explicitly marked false or stock is a real number ≤0.
  const out = product.in_stock === false || (typeof product.stock === 'number' && product.stock <= 0);
  const [notifyOpen, setNotifyOpen] = useState(false);
  const [contact, setContact] = useState('');
  const [reportOpen, setReportOpen] = useState(false);

  const subscribe = async () => {
    if (!contact) { toast.error('Enter your phone or email'); return; }
    try {
      await notifyStock(product.id, contact);
      toast.success("We'll notify you when it's back!");
      setNotifyOpen(false); setContact('');
    } catch { toast.error('Failed to subscribe'); }
  };

  return (
    <div data-testid={`product-card-${product.id}`} className="card-soft p-3 flex gap-3">
      <div className="w-20 h-20 rounded-lg bg-muted overflow-hidden shrink-0">
        {product.image_url ? <img src={product.image_url} alt={product.name} className="w-full h-full object-cover"/> : null}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-heading font-semibold text-ink leading-tight line-clamp-2">{product.name}</div>
        {(product.item_code || product.hsn_india) && (
          <div className="mt-0.5 flex items-center gap-2 text-[10px] text-ink/50 font-mono">
            {product.item_code && <span data-testid={`item-code-${product.id}`} title="Item code">{product.item_code}</span>}
            {product.hsn_india && <span data-testid={`hsn-india-${product.id}`} title="HSN (India GST)">HSN {product.hsn_india}</span>}
            {product.hsn_intl && <span data-testid={`hsn-intl-${product.id}`} title="International HS code">· HS {product.hsn_intl}</span>}
          </div>
        )}
        <div className="mt-1 flex items-center gap-1.5 flex-wrap">
          {product.brand ? (
            <span data-testid={`brand-${product.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#E3F2FD] text-[#0D47A1] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"><Tag className="w-2.5 h-2.5"/>{product.brand}</span>
          ) : (
            <span className="inline-flex items-center gap-1 rounded-full bg-[#E8F5E9] text-[#1B5E20] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider"><Leaf className="w-2.5 h-2.5"/>Local / Loose</span>
          )}
          {product.packaging && (
            <span className="inline-flex items-center gap-1 rounded-full bg-muted text-ink/70 px-2 py-0.5 text-[10px] font-medium"><Package className="w-2.5 h-2.5"/>{packagingLabel(product.packaging)}</span>
          )}
          {product.bulk_min_qty && (
            <span data-testid={`bulk-tag-${product.id}`} className="inline-flex items-center gap-1 rounded-full bg-[#FFF3E0] text-[#D35400] px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider">Bulk ≥{product.bulk_min_qty}{product.bulk_price ? ` · ₹${product.bulk_price}` : ''}</span>
          )}
          {(() => {
            const s = _expiryStatus(product.use_by_date);
            if (!s) return null;
            const cls = {
              expired: 'bg-[#FFEBEE] text-[#C62828] border-[#FFCDD2]',
              soon:    'bg-[#FFF3E0] text-[#BF360C] border-[#FFCCBC]',
              warn:    'bg-[#FFF8E1] text-[#9A7B0A] border-[#FFE082]',
              ok:      'bg-[#E8F5E9] text-[#1B5E20] border-[#A5D6A7]',
            }[s.tone];
            const Icon = s.tone === 'expired' ? AlertTriangle : CalendarDays;
            return (
              <span data-testid={`use-by-${product.id}`} title={`Use-by date · ${s.label}`} className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${cls}`}>
                <Icon className="w-2.5 h-2.5"/>{s.label}
              </span>
            );
          })()}
        </div>
        <div className="text-xs text-muted-foreground mt-0.5">per {product.unit}{out && <span className="ml-2 inline-flex items-center text-destructive font-bold uppercase text-[10px] tracking-wider">Out of stock</span>}</div>
        <div className="mt-2 flex items-center justify-between gap-2">
          <div className="font-bold text-ink">₹{product.price}</div>
          {out ? (
            notifyOpen ? (
              <div className="flex items-center gap-1">
                <input data-testid={`notify-contact-${product.id}`} value={contact} onChange={e=>setContact(e.target.value)} placeholder="Phone / email" className="border border-border rounded-full text-xs px-3 h-8 outline-none focus:border-[#E67E22]"/>
                <button data-testid={`notify-submit-${product.id}`} onClick={subscribe} className="btn-primary !py-1.5 !px-3 !text-xs">OK</button>
              </div>
            ) : (
              <button data-testid={`notify-stock-${product.id}`} onClick={() => setNotifyOpen(true)} className="inline-flex items-center gap-1 rounded-full border border-border text-ink/70 hover:border-[#E67E22] hover:text-[#D35400] px-3 h-8 text-xs font-semibold"><Bell className="w-3.5 h-3.5"/>Notify me</button>
            )
          ) : inCart ? (
            <div className="inline-flex items-center gap-2 bg-[#E67E22] text-white rounded-full h-8 px-1">
              <button data-testid={`product-decrement-${product.id}`} onClick={() => cart.setQty(product.id, inCart.qty - 1)} className="w-7 h-7 grid place-items-center rounded-full hover:bg-[#D35400]"><Minus className="w-3.5 h-3.5"/></button>
              <span data-testid={`product-qty-${product.id}`} className="text-xs font-bold min-w-[14px] text-center">{inCart.qty}</span>
              <button data-testid={`product-increment-${product.id}`} onClick={() => cart.setQty(product.id, inCart.qty + 1)} className="w-7 h-7 grid place-items-center rounded-full hover:bg-[#D35400]"><Plus className="w-3.5 h-3.5"/></button>
            </div>
          ) : (
            <button data-testid={`add-to-cart-${product.id}`} onClick={() => cart.addItem(store, product)} className="inline-flex items-center gap-1 rounded-full border border-[#E67E22] text-[#D35400] hover:bg-[#E67E22] hover:text-white px-3 h-8 text-xs font-semibold transition">
              <Plus className="w-3.5 h-3.5"/> Add
            </button>
          )}
        </div>
        {/* Specifications strip — manufacturing/batch info that customers need by law */}
        {(product.mfg_date || product.batch_no) && (
          <div data-testid={`specs-${product.id}`} className="mt-1.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[10px] text-ink/55 font-medium">
            {product.mfg_date && (
              <span data-testid={`mfg-date-${product.id}`}>MFG <b className="text-ink/75">{fmtDate(product.mfg_date)}</b></span>
            )}
            {product.batch_no && (
              <span data-testid={`batch-${product.id}`}>Batch <b className="text-ink/75 font-mono">{product.batch_no}</b></span>
            )}
          </div>
        )}
        {/* Nearby Pharmacy Alert — only for medical items that are out of stock */}
        {out && (store?.category === 'medical' || product.shop_type === 'medical') && (
          <NearbyPharmacyAlert productId={product.id} productName={product.name}/>
        )}
        {/* Report illegal listing — visible to every signed-in customer, NOT for the store's own owner */}
        {user && store?.owner_id !== user.id && (
          <button
            data-testid={`report-listing-${product.id}`}
            onClick={() => setReportOpen(true)}
            title="Report this listing as illegal or unsafe — admin reviews + 50 MandiCoins reward on validation"
            className="mt-1.5 inline-flex items-center gap-1 text-[10px] text-ink/45 hover:text-[#BF360C] uppercase tracking-wider font-semibold"
          >
            <ShieldAlert className="w-3 h-3"/>Report illegal listing
          </button>
        )}
      </div>
      {reportOpen && (
        <ReportListingDialog product={product} onClose={() => setReportOpen(false)}/>
      )}
    </div>
  );
}

function ReportListingDialog({ product, onClose }) {
  const [category, setCategory] = useState('counterfeit');
  const [reason, setReason] = useState('');
  const [evidence, setEvidence] = useState('');
  const [busy, setBusy] = useState(false);

  const CATS = [
    ['narcotics', 'Narcotics / drugs (NDPS Act)'],
    ['arms_explosives', 'Arms, ammunition or explosives'],
    ['wildlife', 'Wildlife / animal products'],
    ['counterfeit', 'Counterfeit / first-copy / fake brand'],
    ['banned_chemicals', 'Banned pesticide / chemical'],
    ['unauthorised_drugs', 'Prescription drugs sold without Rx'],
    ['tobacco', 'Banned tobacco / gutka / khaini'],
    ['obscene', 'Obscene / adult content (IT Act §67)'],
    ['gambling', 'Gambling / lottery device'],
    ['liquor_no_licence', 'Liquor / alcohol without licence'],
    ['human_organs', 'Human organs / blood for sale'],
    ['expired_unsafe', 'Expired / unsafe / spoiled goods'],
    ['fake_reviews', 'Fake reviews or price manipulation'],
    ['other', 'Other (explain in reason)'],
  ];

  const submit = async () => {
    if (reason.trim().length < 10) { toast.error('Please describe the issue (min 10 chars)'); return; }
    setBusy(true);
    try {
      await api.post(`/products/${product.id}/report`, { category, reason: reason.trim(), evidence_url: evidence.trim() || null });
      toast.success('Report submitted. Admin will review. You earn 50 MandiCoins if validated.');
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Could not submit report');
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/40 grid place-items-center p-4" onClick={onClose}>
      <div data-testid="report-dialog" className="bg-white w-full max-w-md rounded-2xl p-6 shadow-2xl" onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-[0.2em] font-bold text-[#BF360C]"><ShieldAlert className="w-3.5 h-3.5"/>Report illegal listing</div>
            <h3 className="font-heading font-bold text-xl mt-1">{product.name}</h3>
            <p className="text-xs text-ink/60 mt-1">Help us keep BharatCart safe. Admin reviews every report. Validated reports earn you <b>50 MandiCoins</b>.</p>
          </div>
          <button onClick={onClose} className="text-ink/40 hover:text-ink"><X className="w-5 h-5"/></button>
        </div>

        <div className="mt-4 space-y-3 text-sm">
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink/60 mb-1">Reason category</div>
            <select data-testid="report-category" value={category} onChange={e => setCategory(e.target.value)} className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none">
              {CATS.map(([id, label]) => <option key={id} value={id}>{label}</option>)}
            </select>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink/60 mb-1">What's wrong with this listing?</div>
            <textarea data-testid="report-reason" rows={3} value={reason} onChange={e => setReason(e.target.value)} placeholder="Briefly describe — what you saw, why it appears illegal, etc." className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none"/>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-ink/60 mb-1">Evidence URL (optional)</div>
            <input data-testid="report-evidence" value={evidence} onChange={e => setEvidence(e.target.value)} placeholder="Link to photo / video / news article" className="w-full rounded-lg border border-border bg-white px-3 py-2 text-sm outline-none"/>
          </div>
          <div className="rounded-lg bg-[#FFF3E0] border border-[#FFCC80] text-[10px] text-[#9A4500] p-2">
            False or malicious reports may attract account suspension. Be honest and factual.
          </div>
        </div>

        <div className="mt-5 flex gap-2 justify-end">
          <button onClick={onClose} className="btn-secondary !py-2 !text-sm">Cancel</button>
          <button data-testid="report-submit" disabled={busy} onClick={submit} className="btn-primary !py-2 !text-sm">
            {busy ? 'Submitting…' : 'Submit report'}
          </button>
        </div>
      </div>
    </div>
  );
}
